import get from 'lodash.get'
import { formOptions } from '../../../forms/blocks'

export const FIXED_DESIGN_OTHER_OPTION_VALUE = '__other__'

/**
 * Get the product type key from an order product
 * @param {Object} prod - Order product object
 * @returns {string|null} Product type key (e.g. 'shirt', 'custom', 'fixed_design', 'collection')
 */
export const getFromKey = (prod) =>
  prod.orderProductType === 'custom'
    ? prod.orderCustom?.type
    : prod.orderProductType

/**
 * Get the product category from fixed_design details
 * Used to resolve the actual product type (e.g. cashmere, shirt) from fixed design products
 * @param {Object} orderProduct - Order product object
 * @returns {string|null} Product category key (e.g. 'cashmere', 'shirt') or null
 */
export const getFixedDesignCategory = (orderProduct) => {
  if (orderProduct?.orderProductType !== 'fixed_design') return null
  const details = get(orderProduct, 'orderFixedDesign.details', [])
  if (details.length === 0) return null
  const firstDetail = details[0]
  const productTypeKeys = Object.keys(firstDetail).filter((key) => key !== '_id')
  return productTypeKeys.length > 0 ? productTypeKeys[0] : null
}

/**
 * Get category key from a fixed design item (from API)
 * @param {Object} item - Fixed design item from API
 * @returns {string|null} Category key or null
 */
export const getCategoryFromFixedDesign = (item) => {
  const directCategory = get(item, 'category')
  if (directCategory != null) {
    return typeof directCategory === 'object' ? directCategory?.key ?? directCategory?._id : directCategory
  }
  const details = get(item, 'details', [])
  if (!details.length) return null
  const firstDetail = details[0]
  const keys = Object.keys(firstDetail || {}).filter((k) => k !== '_id')
  return keys.length > 0 ? keys[0] : null
}

/**
 * Build measure forms list from product category blocks (e.g. for collection)
 * @param {string[]} blocks - Category block keys
 * @returns {Object[]} Form config array
 */
export const getCollectFrom = (blocks) => {
  let buildForm = []
  if (!blocks || !Array.isArray(blocks)) return buildForm
  blocks.forEach((e) => {
    if (formOptions[e]?.measuresForms) {
      buildForm = [...buildForm, ...formOptions[e].measuresForms]
    }
  })
  return buildForm
}

/**
 * Build form config list from product category blocks (e.g. for collection)
 * @param {string[]} blocks - Category block keys
 * @returns {Object[]} Form config array
 */
export const getCollectFromForm = (blocks) => {
  let buildForm = []
  if (!blocks || !Array.isArray(blocks)) return buildForm
  blocks.forEach((e) => {
    if (formOptions[e]?.forms) {
      buildForm = [...buildForm, ...formOptions[e].forms]
    }
  })
  return buildForm
}

/**
 * Convert selectedOptions array (from orderFixedDesign) to a map { optionId: value }
 * for use in option dropdowns/inputs. value is freeFieldValue or subOptionRef.
 * @param {Array} selectedOptionsArray - orderFixedDesign.selectedOptions
 * @returns {Object} Map of optionId -> current value
 */
export const selectedOptionsToMap = (selectedOptionsArray) => {
  if (!Array.isArray(selectedOptionsArray)) return {}
  const map = {}
  selectedOptionsArray.forEach((item) => {
    if (item?.optionId != null) {
      const customFromName =
        item?.subOptionRef === 'custom'
          ? item?.subOptionName?.en ?? item?.subOptionName?.zh ?? ''
          : ''
      const freeTextValue = item.freeFieldValue ?? customFromName
      if (freeTextValue != null && freeTextValue !== '') {
        map[item.optionId] = FIXED_DESIGN_OTHER_OPTION_VALUE
        map[`${item.optionId}__freeFieldValue`] = freeTextValue
      } else {
        map[item.optionId] = item.subOptionRef ?? ''
      }
    }
  })
  return map
}

/**
 * Format options map (from UI state) to selectedOptions array for orderFixedDesign.
 * @param {Object} optionsObj - Map of optionId -> value (free text or subOption ref)
 * @param {Object} product - Fixed design product with options array
 * @returns {Array} selectedOptions for orderFixedDesign
 */
export const formatSelectedOptions = (optionsObj, product) => {
  if (!product?.options || !Array.isArray(product.options)) return []
  return product.options
    .map((option) => {
      const selectedValue = optionsObj?.[option._id]
      if (selectedValue == null || selectedValue === '') return null
      if (selectedValue === FIXED_DESIGN_OTHER_OPTION_VALUE) {
        const freeFieldValue = optionsObj?.[`${option._id}__freeFieldValue`]
        if (freeFieldValue == null || freeFieldValue === '') return null
        return {
          optionId: option._id,
          optionName: option.name,
          subOptionId: null,
          subOptionRef: 'custom',
          subOptionName: {
            en: freeFieldValue,
            zh: freeFieldValue,
          },
          freeFieldValue,
        }
      }
      const subOption = option.subOptions?.find((sub) => sub.ref === selectedValue)
      if (!subOption) return null
      return {
        optionId: option._id,
        optionName: option.name,
        subOptionId: subOption._id,
        subOptionName: subOption.name,
        subOptionRef: subOption.ref,
      }
    })
    .filter(Boolean)
}

/**
 * Get stock quantity for a selected size from a product that has a stock array.
 * Product stock shape: [{ size_id, size_name_en, size_name_zh, stock }]
 * Product sizes shape: [{ name: { en, zh }, value, _id }]
 * @param {Object} product - Product with .stock (and optionally .sizes)
 * @param {Object|string} selectedSize - Either { name: { en, zh }, value } (e.g. collection) or a size value string (e.g. form)
 * @returns {number|null} Stock quantity for that size, or null if not found / no stock array
 */
export const getStockForSelectedSize = (product, selectedSize) => {
  const stockList = product?.stock
  if (!Array.isArray(stockList) || stockList.length === 0) return null

  let valueToMatch = null
  let sizeIdToMatch = null
  let nameKeyToMatch = null

  if (selectedSize != null && typeof selectedSize === 'object') {
    valueToMatch = selectedSize.value
    sizeIdToMatch = selectedSize._id ?? selectedSize.id ?? null
    if (selectedSize.name) {
      const en = selectedSize.name?.en
      const zh = selectedSize.name?.zh
      nameKeyToMatch = [en, zh].filter(Boolean).join('|')
    }
  } else if (typeof selectedSize === 'string') {
    valueToMatch = selectedSize
    const sizeObj = product?.sizes?.find((s) => s.value === selectedSize)
    sizeIdToMatch = sizeObj?._id ?? sizeObj?.id ?? null
    if (sizeObj?.name) {
      const en = sizeObj.name?.en
      const zh = sizeObj.name?.zh
      nameKeyToMatch = [en, zh].filter(Boolean).join('|')
    }
  }

  // Prefer stable size_id match, then keep temporary fallback by legacy keys during transition.
  let entry = null
  if (sizeIdToMatch != null && sizeIdToMatch !== '') {
    entry = stockList.find((s) => String(s.size_id) === String(sizeIdToMatch))
  }
  if (valueToMatch != null && valueToMatch !== '') {
    entry = entry ?? stockList.find((s) => s.size_key === valueToMatch)
  }
  if (entry == null && nameKeyToMatch) {
    entry = stockList.find(
      (s) =>
        s.size_key === nameKeyToMatch ||
        (s.size_name_en && s.size_name_zh && `${s.size_name_en}|${s.size_name_zh}` === nameKeyToMatch)
    )
  }
  return entry != null ? entry.stock : null
}

/**
 * Compute the total requested quantity for a fixed-SKU product+size across an order.
 * Useful to warn when users add duplicates beyond available stock.
 *
 * @param {Object} params
 * @param {Array} params.orderProducts - values.orderProducts
 * @param {string} params.productId - orderCollection.product id
 * @param {string} params.selectedSizeValue - size value string (e.g. 'S', '40')
 * @param {number|null} [params.excludeIndex] - exclude one line item index (e.g. currently edited row)
 * @returns {number} requested quantity (>= 0)
 */
export const getRequestedFixSkuQtyForSize = ({
  orderProducts,
  productId,
  selectedSizeValue,
  excludeIndex = null,
}) => {
  if (!Array.isArray(orderProducts) || !productId || selectedSizeValue == null || selectedSizeValue === '') {
    return 0
  }

  let total = 0
  orderProducts.forEach((prod, idx) => {
    if (excludeIndex != null && idx === excludeIndex) return
    if (!prod || prod.orderProductType !== 'collection') return

    const pid = prod?.orderCollection?.product
    if (!pid || String(pid) !== String(productId)) return

    const size = prod?.orderCollection?.size
    const sizeValue =
      size != null && typeof size === 'object'
        ? size.value
        : size

    if (sizeValue == null || sizeValue === '') return
    if (String(sizeValue) !== String(selectedSizeValue)) return

    const qtyRaw = prod?.orderCollection?.quantity
    const qty =
      typeof qtyRaw === 'number' && !Number.isNaN(qtyRaw) && qtyRaw > 0
        ? qtyRaw
        : 1
    total += qty
  })

  return total
}
